<script setup lang="ts">
import { computed } from "vue";
import { UiButton, UiIcon, UiSelect, UiText } from "@shopexperts/shared";
import type { BannedEmail, BannedEmailSource } from "@shopexperts/legacy-api";
import BanListSearchInput from "./BanListSearchInput.vue";
import BanListEmptyState from "./BanListEmptyState.vue";
import BanListLoadMore from "./BanListLoadMore.vue";
import BannedEmailCard from "./BannedEmailCard.vue";

const props = defineProps<{
    bans: BannedEmail[];
    ruleOptions: { label: string; value: string }[];
    isLoading?: boolean;
    isFetching?: boolean;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
    hasFilters?: boolean;
    isBusy?: boolean;
}>();

const emit = defineEmits<{
    (e: "create"): void;
    (e: "unban", ban: BannedEmail): void;
    (e: "filter-by-rule", ruleId: number): void;
    (e: "load-more"): void;
    (e: "reset-filters"): void;
}>();

const searchInput = defineModel<string>("searchInput", { required: true });
const sourceFilter = defineModel<BannedEmailSource | "">("sourceFilter", { required: true });
const ruleFilter = defineModel<string>("ruleFilter", { required: true });

const sourceOptions: readonly { label: string; value: string }[] = [
    { label: "All sources", value: "" },
    { label: "Rule", value: "rule" },
    { label: "Manual", value: "manual" },
];

const ruleSelectOptions = computed(() => [
    { label: "All rules", value: "" },
    ...props.ruleOptions,
]);
</script>

<template>
    <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <BanListSearchInput v-model="searchInput" placeholder="Search by email..." />

                <div class="w-full sm:w-[170px]">
                    <UiSelect
                        v-model="sourceFilter"
                        :options="(sourceOptions as { label: string; value: string }[])"
                        placeholder="All sources"
                        size="md"
                    />
                </div>

                <div class="w-full sm:w-[200px]">
                    <UiSelect v-model="ruleFilter" :options="ruleSelectOptions" placeholder="All rules" size="md" />
                </div>

                <UiButton v-if="hasFilters" variant="ghost" label="Clear filters" @click="emit('reset-filters')" />
            </div>

            <UiButton variant="primary" label="Ban Email" @click="emit('create')">
                <template #before_label>
                    <UiIcon name="plus-square" class="h-5 w-5 text-se-primary" />
                </template>
            </UiButton>
        </div>

        <UiText
            as="p"
            variant="caption-normal"
            class="text-se-gray-500"
            text="A banned address stays blocked on later submissions even when the text is clean. Unbanning also restores the account behind it."
        />

        <div v-if="isLoading" class="py-12 text-center">
            <UiText as="p" variant="p" class="text-se-gray-600" text="Loading banned emails..." />
        </div>

        <BanListEmptyState
            v-else-if="bans.length === 0"
            icon="lock"
            :title="hasFilters ? 'No bans match these filters' : 'No banned emails'"
            :description="hasFilters
                ? 'Try another address, source or rule — or clear the filters.'
                : 'Addresses land here automatically when a rule fires on an attributable submitter, or when an admin bans one by hand.'"
            :action-label="hasFilters ? undefined : 'Ban Email'"
            @action="emit('create')"
        />

        <template v-else>
            <BannedEmailCard
                v-for="ban in bans"
                :key="ban.id"
                :ban="ban"
                :is-busy="isBusy"
                @unban="emit('unban', ban)"
                @filter-by-rule="(ruleId: number) => emit('filter-by-rule', ruleId)"
            />

            <BanListLoadMore
                :has-next-page="hasNextPage"
                :is-fetching-next-page="isFetchingNextPage"
                :is-refreshing="isFetching && !isLoading && !isFetchingNextPage"
                label="Load more bans"
                @load-more="emit('load-more')"
            />
        </template>
    </div>
</template>
